import { useLocale } from '@solinkcorp/locale';
import { Checkbox } from '@solinkcorp/solink-ui/components/Checkbox';
import { Input } from '@solinkcorp/solink-ui/components/Input';
import { NumberInputGroup } from '@solinkcorp/solink-ui/components/NumberInputGroup';
import { cn } from '@solinkcorp/solink-ui/lib/utils';
import React from 'react';
import { NO_RANGE_FOR_DAY } from '../../day-part/constants';
import {
  formatTimeRangeForDay,
  type DayIndex,
  type ServiceDisplayDayPart,
} from '../../day-part/utils';
import { ThresholdConflictMessage } from './ThresholdConflictMessage';

export type WidgetThresholdType = 'COUNT' | 'TIME' | 'DRIVE_OFF';

interface ThresholdRowProps {
  dayPart: ServiceDisplayDayPart;
  widgetType: WidgetThresholdType;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  /** When true, checkbox cannot be turned on (overlap with another enabled day part on same day). */
  disabled?: boolean;
  /** Shown when disabled due to schedule overlap. */
  conflictMessage?: string;
  /** When > 1, show tooltip with full message for readability. */
  conflictLabelCount?: number;
  /** For COUNT and DRIVE_OFF: single numeric threshold */
  countThreshold: number;
  onCountThresholdChange: (value: number) => void;
  /** For TIME: minutes and seconds */
  timeThresholdMinutes: number;
  timeThresholdSeconds: number;
  onTimeThresholdChange: (minutes: number, seconds: number) => void;
}

/** Current day of week as DayIndex (0 = Sunday, 6 = Saturday) for "today" time range display */
function getTodayDayIndex(): DayIndex {
  const d = new Date().getDay();
  return String(d) as DayIndex;
}

export const ThresholdRow: React.FC<ThresholdRowProps> = ({
  dayPart,
  widgetType,
  checked,
  onCheckedChange,
  disabled = false,
  conflictMessage,
  conflictLabelCount = 0,
  countThreshold,
  onCountThresholdChange,
  timeThresholdMinutes,
  timeThresholdSeconds,
  onTimeThresholdChange,
}) => {
  const locale = useLocale();
  const todayDayIndex = getTodayDayIndex();
  const rawRange = formatTimeRangeForDay(dayPart.dayParts, todayDayIndex);
  const timeRangeLabel =
    rawRange === NO_RANGE_FOR_DAY
      ? locale.serviceDisplayMonitor.dayPartNotScheduledToday
      : rawRange;
  const isTimeType = widgetType === 'TIME';
  // DRIVE_OFF rows are association-only: just the checkbox + label, no threshold value input.
  const isAssignmentOnly = widgetType === 'DRIVE_OFF';

  const handleCheckedChange = (value: boolean | 'indeterminate') => {
    if (disabled && value === true) return;
    onCheckedChange(value === true);
  };

  return (
    <div
      className="flex w-full items-center gap-4"
      data-testid={`day-part-threshold-row-${dayPart.label}`}
    >
      <div className="flex min-w-0 flex-1 items-start gap-2">
        <Checkbox
          checked={checked}
          onCheckedChange={handleCheckedChange}
          disabled={disabled}
          className="mt-0.5 size-5 min-h-5 min-w-5 shrink-0 rounded-[4px] text-interaction-primary-content-default"
          aria-label={dayPart.label}
          aria-describedby={conflictMessage ? `${dayPart.id}-conflict` : undefined}
        />
        <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 self-stretch">
          <p className="truncate text-sm font-medium leading-[20px] text-foreground">
            {dayPart.label}
          </p>
          <p className="text-xs font-normal leading-4 text-text-secondary">
            {timeRangeLabel + '*'}
          </p>
          {conflictMessage && (
            <ThresholdConflictMessage
              message={conflictMessage}
              labelCount={conflictLabelCount ?? 0}
              id={`${dayPart.id}-conflict`}
            />
          )}
        </div>
      </div>
      {!isAssignmentOnly && (
        <div
          className={cn(
            'flex min-w-0 flex-1 shrink-0 items-center justify-end gap-2',
            (disabled || !checked) && 'pointer-events-none opacity-50',
          )}
          aria-disabled={disabled || !checked}
          data-testid={`day-part-threshold-override`}
        >
          {isTimeType ? (
            <>
              <NumberInputGroup
                placeholder="0"
                min={0}
                value={timeThresholdMinutes}
                onChange={(value) =>
                  onTimeThresholdChange(Number(value ?? 0), timeThresholdSeconds)
                }
                suffix="min"
                className="min-w-0 flex-1"
              />
              <span className="shrink-0 text-sm font-medium leading-5 text-foreground">:</span>
              <NumberInputGroup
                placeholder="0"
                min={0}
                max={59}
                value={timeThresholdSeconds}
                onChange={(value) =>
                  onTimeThresholdChange(timeThresholdMinutes, Number(value ?? 0))
                }
                suffix="sec"
                className="min-w-0 flex-1"
              />
            </>
          ) : (
            <Input
              type="number"
              min={0}
              value={countThreshold}
              onChange={(e) => onCountThresholdChange(Number(e.currentTarget.value) || 0)}
              className={cn(
                'mr-4 min-w-0 max-w-[84px] flex-1 text-text-primary',
                'focus-visible:border-teal-500 focus-visible:ring-[3px] focus-visible:ring-teal-500/50',
              )}
              disabled={disabled || !checked}
            />
          )}
        </div>
      )}
    </div>
  );
};
